/**
 * src/lib/offline/entityCache.js — domain-agnostic write-through cache.
 *
 * Generalizes the original task cache so EVERY offline collection (tasks,
 * projects, bundles, crm, vessels, vesselContacts, clients, people, ports, ...)
 * reuses one implementation over the shared `entities` IndexedDB store.
 *
 * Policy (identical to the task-first design, now per collection):
 *   - write-through ONLY when online AND the fetch returned rows — never cache
 *     an empty/failed result (the api fetchers swallow errors -> []), which would
 *     wipe a good offline cache on a transient failure.
 *   - hydrate from cache ONLY when offline. When online we trust the live result.
 */

import { idbReadDomain, idbReplaceDomain, idbClearDomain } from './db';

/**
 * Build a cache handle bound to one collection key (the `domain`).
 * @param {string} collection e.g. 'tasks', 'projects', 'crm'
 */
export function makeEntityCache(collection) {
  return {
    collection,
    /** Replace the cached rows for this collection. Best-effort, tolerant of junk. */
    async write(rows) {
      if (!Array.isArray(rows)) return undefined;
      return idbReplaceDomain(collection, rows);
    },
    /** Read cached rows for this collection. Returns [] on any error. */
    async read() {
      try {
        const rows = await idbReadDomain(collection);
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      }
    },
    /** Empty this collection's cache. */
    async clear() {
      return idbClearDomain(collection);
    },
    /**
     * Decide what to return for a fetch and update the cache.
     * @param {{ fetched: any[], online: boolean }} input
     * @returns {Promise<{ rows: any[], fromCache: boolean }>}
     */
    async reconcile({ fetched, online }) {
      const arr = Array.isArray(fetched) ? fetched : [];
      if (online) {
        if (arr.length) {
          await this.write(arr).catch(() => {});
          return { rows: fetched, fromCache: false };
        }
        // Online but the fetch came back EMPTY. The api fetchers swallow
        // network/auth errors into [] (see api/crm.js), so an empty online
        // result is far more likely a transient failure than a genuinely
        // empty collection. We already refuse to WRITE it over a good cache
        // (policy above); likewise refuse to RETURN it over a populated cache
        // — otherwise a flaky sync blanks the live view (CEO 2026-07-13:
        // "synced before takeoff and the pipeline disappeared"). The cache is
        // left intact so the next good fetch reconciles normally.
        // ponytail: a genuinely-emptied collection keeps showing cache until
        // the next non-empty fetch — acceptable vs. wiping the whole view on
        // every transient blip. Real empty-vs-error signalling is TICKET-262.
        const cachedOnline = await this.read();
        if (cachedOnline.length) return { rows: cachedOnline, fromCache: true, staleEmpty: true };
        return { rows: arr, fromCache: false };
      }
      const cached = await this.read();
      if (cached.length) return { rows: cached, fromCache: true };
      return { rows: arr, fromCache: false };
    },
  };
}

/**
 * Convenience for domain hooks: reconcile one collection and return JUST the
 * array (the common case inside a fetchAll that handles several collections).
 *
 * @param {string} collection
 * @param {any[]} fetched
 * @param {boolean} online
 * @returns {Promise<any[]>}
 */
export async function reconcileCollection(collection, fetched, online) {
  try {
    const { rows } = await makeEntityCache(collection).reconcile({ fetched, online });
    return rows;
  } catch {
    // Best-effort: never degrade the live result on a cache error.
    return Array.isArray(fetched) ? fetched : [];
  }
}
